import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Chip, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { BADGE_COLORS, getAlmacenColor, getBadgeStyles } from "../../../../components/ui/badgeColors";
import { calcularDiferencia } from "../../helpers/transformers";
import { conteosDeLinea, diferenciaDeLinea } from "../../helpers/conteos";
import { useAlmacenes } from "../../hooks/useAlmacenes";
import DiferenciaBadge from "./DiferenciaBadge";
import StockAlmacenes from "./StockAlmacenes";
import ConteosPorNave from "./ConteosPorNave";
import SelectorNaveArticulo from "./SelectorNaveArticulo";
import UbicacionArticulo from "./UbicacionArticulo";
import { normalizarCodigoAlmacen } from "../../utils/formatters";

const MENSAJE_ENTERO_INVALIDO = "Introduce un número entero igual o mayor que 0.";

/** Id del DOM de la tarjeta de una línea, para llevar al usuario hasta ella */
export const idTarjetaConteo = (lineaId) => `linea-conteo-${lineaId}`;

/**
 * Tarjeta de un artículo en ConteoInventario. Se cuenta nave a nave, en la nave
 * general de la pantalla salvo que se cambie solo para este artículo
 * (SelectorNaveArticulo); el campo muestra lo ya contado EN ESA nave,
 * que se suma de uno en uno (+1, o punteando desde el buscador) o se escribe a
 * mano. Arriba, el stock actual de cada nave y lo contado en cada una (pulsar un
 * conteo cambia a esa nave; su aspa lo quita, vía onQuitarNave(linea, conteo)). Los cambios van a un borrador de la página y no se
 * guardan hasta pulsar Guardar; mientras tanto la tarjeta se marca en ámbar.
 *
 * `soloLectura` la reutiliza en modo resultado: el resultado nave a nave.
 * Envuelta en React.memo: cada tarjeta solo se repinta si cambia SU línea. Por eso
 * los callbacks reciben el id de la línea (o la línea) en vez de venir ya atados:
 * así la página puede pasar siempre las mismas funciones a todas las tarjetas.
 */
const TarjetaArticuloConteo = React.forwardRef(
    (
        {
            linea,
            pendiente = false,
            error = null,
            almacenSeleccionado = null,
            almacenGeneral = null,
            stockPorAlmacen,
            ubicacion,
            onFijar,
            onSumar,
            onSeleccionarAlmacen,
            disabled = false,
            soloLectura = false,
            quitarDeshabilitado = false,
            onQuitarNave,
        },
        ref
    ) => {
        const { almacenes, getNombreAlmacen } = useAlmacenes();
        const conteos = conteosDeLinea(linea);
        const conteoActual = conteos.find((conteo) => conteo.almacen === almacenSeleccionado);
        const cantidadAlmacen = conteoActual?.cantidadContada ?? null;

        const [valor, setValor] = useState(cantidadAlmacen ?? "");
        const [errorLocal, setErrorLocal] = useState(null);

        // Cambiar de nave, puntear o guardar cambia la cantidad desde fuera: el campo la refleja
        useEffect(() => {
            setValor(cantidadAlmacen ?? "");
            setErrorLocal(null);
        }, [cantidadAlmacen, almacenSeleccionado]);

        // Todo el catálogo, no solo los almacenes con stock: se puede contar donde el sistema dice 0
        const opcionesAlmacen = useMemo(() => {
            const codigos = new Set([
                ...almacenes.map((almacen) => normalizarCodigoAlmacen(almacen.CODIGO)),
                ...Object.keys(stockPorAlmacen ?? {}),
                ...(almacenSeleccionado ? [almacenSeleccionado] : []),
            ]);
            return [...codigos].sort();
        }, [almacenes, stockPorAlmacen, almacenSeleccionado]);

        const confirmar = () => {
            const texto = String(valor ?? "").trim();
            if (texto === "") {
                setValor(cantidadAlmacen ?? "");
                return;
            }
            if (!/^\d+$/.test(texto)) {
                setErrorLocal(MENSAJE_ENTERO_INVALIDO);
                return;
            }
            setErrorLocal(null);
            const numero = Number(texto);
            if (numero !== cantidadAlmacen) onFijar(linea.id, numero);
        };

        const handleKeyDown = (evento) => {
            if (evento.key === "Enter") {
                evento.preventDefault();
                confirmar();
            }
        };

        const mensajeError = errorLocal || error;
        // El congelado si esa nave ya se contó (el backend lo conserva al recontar), si no el actual de Eurowin
        let stockTeorico = null;
        if (almacenSeleccionado) {
            stockTeorico = conteoActual?.stockTeorico ?? (stockPorAlmacen ? stockPorAlmacen[almacenSeleccionado] ?? 0 : null);
        }
        const diferenciaNave = calcularDiferencia(stockTeorico, cantidadAlmacen);
        const nombreNave = almacenSeleccionado ? getNombreAlmacen(almacenSeleccionado) : null;
        // Solo se destaca lo que falta por guardar: un check de "contado" se leía como inventario completo
        const borde = pendiente ? `1px solid ${BADGE_COLORS.AMBER.border}` : "1px solid transparent";

        return (
            <Card ref={ref} id={idTarjetaConteo(linea.id)} sx={{ p: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: borde }}>
                <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#212529" }}>{linea.referencia}</Typography>
                            <Typography sx={{ fontSize: 13, color: "#6c757d" }}>{linea.nombre}</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <UbicacionArticulo ubicacion={ubicacion} />
                            </Box>
                        </Box>
                        {pendiente && (
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: BADGE_COLORS.AMBER.text }}>Sin guardar</Typography>
                        )}
                    </Stack>

                    {soloLectura ? (
                        <>
                            <ConteosPorNave conteos={conteos} />
                            {conteos.length > 1 && <TotalLinea linea={linea} />}
                        </>
                    ) : (
                        <>
                            <StockAlmacenes stockPorAlmacen={stockPorAlmacen} />

                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: "#495057" }}>
                                    Contado por nave{linea.cantidadContada != null ? ` · total ${linea.cantidadContada}` : ""}
                                </Typography>
                                {conteos.length === 0 ? (
                                    <Typography sx={{ fontSize: 12, color: "#6c757d" }}>Sin contar en ninguna nave</Typography>
                                ) : (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                        {conteos.map((conteo) => {
                                            const activo = conteo.almacen === almacenSeleccionado;
                                            return (
                                                <Chip
                                                    key={conteo.almacen}
                                                    label={`${getNombreAlmacen(conteo.almacen)}: ${conteo.cantidadContada}${conteo.pendiente ? " *" : ""}`}
                                                    size="small"
                                                    onClick={disabled ? undefined : () => onSeleccionarAlmacen(linea.id, conteo.almacen)}
                                                    onDelete={disabled || quitarDeshabilitado ? undefined : () => onQuitarNave(linea, conteo)}
                                                    deleteIcon={<CloseIcon aria-label={`Quitar ${getNombreAlmacen(conteo.almacen)}`} />}
                                                    sx={{
                                                        ...getBadgeStyles(getAlmacenColor(conteo.almacen), { height: 24, fontSize: 11 }),
                                                        ...(activo && { border: `2px solid ${getAlmacenColor(conteo.almacen).border}` }),
                                                        // El hover de MUI en un Chip pulsable lo pinta gris: se conserva el color de la nave
                                                        "&.MuiChip-clickable:hover": {
                                                            backgroundColor: getAlmacenColor(conteo.almacen).bg,
                                                            filter: "brightness(0.95)",
                                                        },
                                                        "& .MuiChip-deleteIcon": { color: getAlmacenColor(conteo.almacen).text, opacity: 0.7 },
                                                        "& .MuiChip-deleteIcon:hover": { color: getAlmacenColor(conteo.almacen).text, opacity: 1 },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                                {conteos.some((conteo) => conteo.pendiente) && (
                                    <Typography sx={{ fontSize: 11, color: "#6c757d", mt: 0.5 }}>* sin guardar</Typography>
                                )}
                            </Box>

                            <Box>
                                <SelectorNaveArticulo
                                    value={almacenSeleccionado}
                                    almacenGeneral={almacenGeneral}
                                    opciones={opcionesAlmacen}
                                    stockPorAlmacen={stockPorAlmacen}
                                    conteos={conteos}
                                    onChange={(codigo) => onSeleccionarAlmacen(linea.id, codigo)}
                                    disabled={disabled}
                                />
                                {almacenSeleccionado && (
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.75 }}>
                                        <Typography sx={{ fontSize: 12, color: "#495057" }}>
                                            Stock teórico en {nombreNave}: <strong>{stockTeorico ?? "-"}</strong>
                                        </Typography>
                                        {diferenciaNave.contado && stockTeorico != null && (
                                            <DiferenciaBadge diferencia={diferenciaNave.diferencia} contado />
                                        )}
                                    </Stack>
                                )}
                            </Box>

                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#495057" }}>
                                    {nombreNave ? `Cantidad contada en ${nombreNave}` : "Cantidad contada"}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={valor}
                                        placeholder={almacenSeleccionado ? "0" : "Elige antes la nave"}
                                        onChange={(evento) => {
                                            setValor(evento.target.value);
                                            if (errorLocal) setErrorLocal(null);
                                        }}
                                        onBlur={confirmar}
                                        onKeyDown={handleKeyDown}
                                        disabled={disabled || !almacenSeleccionado}
                                        error={Boolean(mensajeError)}
                                        helperText={mensajeError || " "}
                                        inputProps={{ min: 0, step: 1, inputMode: "numeric" }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={() => onSumar(linea.id)}
                                        disabled={disabled || !almacenSeleccionado}
                                        aria-label={`Sumar uno a ${linea.referencia}`}
                                        startIcon={<AddIcon />}
                                        sx={{
                                            minWidth: 72,
                                            height: 40,
                                            backgroundColor: "#cc071e",
                                            textTransform: "none",
                                            fontWeight: 700,
                                            "&:hover": { backgroundColor: "#a30618" },
                                        }}
                                    >
                                        1
                                    </Button>
                                </Stack>
                            </Box>
                        </>
                    )}
                </Stack>
            </Card>
        );
    }
);

/**
 * Total de la línea sumando todas las naves, con su diferencia
 */
const TotalLinea = ({ linea }) => {
    const { diferencia, contado } = diferenciaDeLinea(linea);
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#495057" }}>Total contado: {linea.cantidadContada}</Typography>
            <DiferenciaBadge diferencia={diferencia} contado={contado} />
        </Stack>
    );
};

TarjetaArticuloConteo.displayName = "TarjetaArticuloConteo";

export default React.memo(TarjetaArticuloConteo);
